import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAckKaliningradCelebration,
  useFlags,
  useKaliningradCelebration,
  useQualificationCelebration,
} from '../../api/hooks';
import { useCelebrationsIdle } from '../../celebrationGate';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
import { kgdNum } from './Kaliningrad';
import type { KgdCelebration } from '../../api/types';

/** Число «набегает» счётчиком (~600 мс), prefers-reduced-motion — сразу итог. */
function CountUp({ to, reduced }: { to: number; reduced: boolean }) {
  const [v, setV] = useState(reduced ? to : 0);
  useEffect(() => {
    if (reduced) return;
    const start = performance.now();
    const dur = 600;
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      setV(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, reduced]);
  return <>{kgdNum(v)}</>;
}

/**
 * Экран 3 — рекап пул-отбора (8c): полноэкранный оверлей при первом открытии
 * после внесения результатов. Блоки появляются последовательно сверху вниз;
 * пропускает вперёд тап. Показывается после листов достижений/уровня
 * (celebrationGate) и не поверх рекапа Elo-отбора.
 */
export function KaliningradRecap({ enabled }: { enabled: boolean }) {
  const flags = useFlags();
  const on = enabled && Boolean(flags.data?.kaliningrad);
  const celebrationsIdle = useCelebrationsIdle();
  const celebration = useKaliningradCelebration(on);
  // Рекап Сочи делит те же игры: пока он в очереди — не показываемся
  // (после его ack кэш не сбрасывается — наш рекап уедет на следующее открытие).
  const sochi = useQualificationCelebration(enabled);
  const ack = useAckKaliningradCelebration();
  const [dismissed, setDismissed] = useState(false);
  const [step, setStep] = useState(0);
  const reduced = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );
  const hapticsFired = useRef(false);

  const c = celebration.data ?? null;
  const STEPS = 5; // заголовок, пул, очки, зачёт, место+кнопки
  const visible =
    on && celebrationsIdle && !dismissed && c !== null && sochi.isSuccess && sochi.data === null;
  const last = step >= STEPS - 1;

  useEffect(() => {
    if (!visible) return;
    if (reduced) {
      setStep(STEPS - 1);
      return;
    }
    if (last) return;
    const t = setTimeout(() => setStep((s) => Math.min(s + 1, STEPS - 1)), step === 1 ? 900 : 550);
    return () => clearTimeout(t);
  }, [visible, step, last, reduced]);

  useEffect(() => {
    if (!visible || !c) return;
    if (step === 0) track('kgd_recap_shown', { gameId: c.gameId });
    if (!hapticsFired.current && (c.special.enteredZone || c.special.won)) {
      hapticsFired.current = true;
      haptics.success();
    }
  }, [visible, step, c]);

  if (!visible || !c) return null;

  const done = () => {
    void ack.mutateAsync().catch(() => undefined);
    setDismissed(true);
  };
  const skip = () => {
    if (!last) setStep(STEPS - 1);
  };

  return (
    <div className="kq-recap" onClick={skip}>
      <RecapBody c={c} step={step} reduced={reduced} onDone={done} />
      {!last && <div className="q-recap-skip">тап — пропустить</div>}
    </div>
  );
}

function RecapBody({
  c,
  step,
  reduced,
  onDone,
}: {
  c: NonNullable<KgdCelebration>;
  step: number;
  reduced: boolean;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const b = c.poolBreakdown;
  const o = c.outcome;
  const title = c.special.enteredZone
    ? 'Ты в четвёрке'
    : c.special.won
      ? '1-е место'
      : c.place !== null
        ? `Твоё место — ${c.place}`
        : 'Турнир сыгран';

  // Зачёт: новый результат выделен; «не вошёл» — серым справа от ряда.
  const slots: Array<{ v: number | null; isNew: boolean; ghost?: boolean }> = Array.from(
    { length: c.bestCount },
    (_, i) => ({ v: i < c.bestResults.length ? c.bestResults[i] : null, isNew: false }),
  );
  if (o.kind !== 'not_counted') {
    const idx = c.bestResults.findIndex((v) => v === c.points);
    if (idx >= 0) slots[idx].isNew = true;
  }

  return (
    <div className="kq-recap-body" onClick={(e) => (step >= 4 ? e.stopPropagation() : undefined)}>
      <div className={`kqc-block${step >= 0 ? ' in' : ''}`}>
        <div className="kqc-head mono">
          Игра {c.gameSeq} · {c.field} игроков
        </div>
        <div className={`kqc-title${c.special.enteredZone || c.special.won ? ' gold' : ''}`}>{title}</div>
      </div>

      {step >= 1 && (
        <div className="kqc-block in kq-card kqc-pool">
          <div className="kqc-label mono">Пул турнира</div>
          <div className="kqc-sum mono">
            {b.entries} входов · {b.reentries} ре-энтри
          </div>
          <div className="kqc-pooltotal mono">
            <CountUp to={b.pool} reduced={reduced} />
          </div>
          <div className={`kqc-guarantee${b.guaranteeApplied ? ' gold' : ''}`}>
            {b.guaranteeApplied ? 'гарантия' : 'гарантия не понадобилась'}
          </div>
        </div>
      )}

      {step >= 2 && (
        <div className="kqc-block in kqc-points">
          <div className="kqc-label gold">Твои очки</div>
          <div className="kqc-pointsrow">
            <span>
              {c.place !== null ? `${c.place}-е место` : 'без места'}
              {c.points > 0 && c.sharePct > 0 ? ` · ${c.sharePct}% пула` : ''}
            </span>
            <span className="n mono">
              +<CountUp to={c.points} reduced={reduced} />
            </span>
          </div>
        </div>
      )}

      {step >= 3 && (
        <div className="kqc-block in">
          <div className="kqc-zach">
            <span className="kqc-label mono">Зачёт</span>
            <span className="kqc-label mono muted">лучшие {c.bestCount} из 16</span>
          </div>
          <div className="kq-slots kq-slots--mid kqc-slots">
            {slots.map((s, i) =>
              s.v === null ? (
                <span key={i} className="kq-slot kq-slot--empty">·</span>
              ) : (
                <span key={i} className={`kq-slot mono ${s.isNew ? 'kq-slot--new' : s.v === 0 ? 'kq-slot--zero' : 'kq-slot--fill'}`}>
                  {s.v}
                </span>
              ),
            )}
            {o.kind === 'not_counted' && <span className="kq-slot kq-slot--ghost mono">{c.points}</span>}
          </div>
          <div className={`kqc-outcome${o.kind === 'not_counted' ? ' muted' : ' gold'}`}>
            {o.kind === 'free_slot' &&
              (o.eighthFilled
                ? `Зачёт полный — теперь каждая игра заменяет худший результат`
                : `В зачёте, свободно ${o.freeLeft}`)}
            {o.kind === 'replaced' && `Заменил ${kgdNum(o.replacedPoints)} · сумма +${kgdNum(o.sumDelta)}`}
            {o.kind === 'not_counted' && `Не в зачёте: худший из зачётных — ${kgdNum(o.worstCounted)}`}
          </div>
        </div>
      )}

      {step >= 4 && (
        <div className="kqc-block in" onClick={(e) => e.stopPropagation()}>
          <div className="kq-card kqc-place">
            <span className="side">
              <span className="kqc-label mono">Место</span>
              <span className="move mono">
                {c.rankBefore !== null && c.rankBefore !== c.rankAfter ? (
                  <>
                    <i className="old">{c.rankBefore}</i> <i className="arr">→</i> {c.rankAfter}
                  </>
                ) : (
                  c.rankAfter
                )}
              </span>
            </span>
            <span className="side right">
              <span className="kqc-label mono">
                {c.leadOver5th !== null ? 'Отрыв от 5-го' : 'До четвёрки'}
              </span>
              <span className="gap mono">
                {c.leadOver5th !== null
                  ? kgdNum(c.leadOver5th)
                  : c.gapToTop4 !== null
                    ? kgdNum(c.gapToTop4)
                    : '—'}
              </span>
            </span>
          </div>
          <div className="kqc-btns">
            <button className="kqc-btn" onClick={onDone}>
              Готово
            </button>
            <button
              className="kqc-btn kqc-btn--primary"
              onClick={() => {
                track('kgd_register_tap', { from: 'recap' });
                const id = c.nextTournament?.id;
                onDone();
                if (id) navigate(`/tournaments/${id}`);
              }}
            >
              На следующую
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
